import { create } from 'zustand'
import type { FilterGroup, ViewSort } from '@features/views/models'

/**
 * What the user is looking at, for Arvoke AI's page context (decision 16 in
 * backend/docs/architecture/arvoke_ai/arvoke_ai_decisions.md). Pages register
 * what they show; `useAssistantChat` snapshots it with every message.
 *
 * `PageHeader` registers the header of every page automatically. The Tables
 * page, which has no `PageHeader`, registers the table and view instead; any
 * other grid (`TableWorkspace`: External Tables, Runs, Users) registers what
 * it shows as the grid. Save bars, warning bars and error toasts register the
 * messages they show as notices, several at once.
 */
export interface AssistantHeaderContext {
  title: string | null
  breadcrumbs: string[]
  activeTab: string | null
  statuses: string[]
}

export interface AssistantTableContext {
  tableId: string
  /** The saved view, or the unsaved copy of it, the grid shows (null = the default view). */
  viewId: string | null
  hasUnsavedViewChanges: boolean
  filters: FilterGroup
  sorts: ViewSort[]
  groupColumnIds: string[]
  pinnedColumnIds: string[]
  visibleFieldCount: number | null
  totalFieldCount: number | null
  totalRows: number | null
  searchText: string | null
}

/** A grid that isn't a data table, by the names its screen shows. */
export interface AssistantGridContext {
  title: string
  view: string | null
  filterLogic: 'and' | 'or' | 'mixed' | null
  filters: { column: string; operator: string; value: string | null }[]
  sorts: { column: string; direction: 'asc' | 'desc' }[]
  groups: string[]
  columns: string[]
  rowCount: number | null
  searchText: string | null
}

/** Field Settings, open or docked: the field and its unsaved draft, formula included. */
export interface AssistantFieldEditorContext {
  /** The field being edited; null for a new one. */
  fieldId: string | null
  tableId: string | null
  mode: 'create' | 'edit'
  /** The Display Name as drafted. */
  fieldName: string
  tab: string | null
  docked: boolean
  unsavedChanges: boolean
  formula: string
  formulaStatus: 'empty' | 'checking' | 'valid' | 'invalid' | 'unavailable' | null
  formulaProblems: string[]
}

/**
 * Each registration carries the token of the component that made it, so a
 * page that unmounts after the next one mounted can't clear the newcomer's
 * context.
 */
interface Registration<T> {
  owner: symbol
  value: T
}

interface AssistantContextState {
  header: Registration<AssistantHeaderContext> | null
  table: Registration<AssistantTableContext> | null
  grid: Registration<AssistantGridContext> | null
  fieldEditor: Registration<AssistantFieldEditorContext> | null
  /** Messages on screen, each registrant's in the order they registered. */
  notices: Registration<string[]>[]
  setHeader: (owner: symbol, value: AssistantHeaderContext) => void
  clearHeader: (owner: symbol) => void
  setTable: (owner: symbol, value: AssistantTableContext) => void
  clearTable: (owner: symbol) => void
  setGrid: (owner: symbol, value: AssistantGridContext) => void
  clearGrid: (owner: symbol) => void
  setFieldEditor: (owner: symbol, value: AssistantFieldEditorContext) => void
  clearFieldEditor: (owner: symbol) => void
  setNotices: (owner: symbol, value: string[]) => void
  clearNotices: (owner: symbol) => void
}

export const useAssistantContextStore = create<AssistantContextState>((set) => ({
  header: null,
  table: null,
  setHeader: (owner, value) => set({ header: { owner, value } }),
  clearHeader: (owner) => set((state) => (state.header?.owner === owner ? { header: null } : {})),
  setTable: (owner, value) => set({ table: { owner, value } }),
  clearTable: (owner) => set((state) => (state.table?.owner === owner ? { table: null } : {})),
  grid: null,
  setGrid: (owner, value) => set({ grid: { owner, value } }),
  clearGrid: (owner) => set((state) => (state.grid?.owner === owner ? { grid: null } : {})),
  fieldEditor: null,
  setFieldEditor: (owner, value) => set({ fieldEditor: { owner, value } }),
  clearFieldEditor: (owner) =>
    set((state) => (state.fieldEditor?.owner === owner ? { fieldEditor: null } : {})),
  notices: [],
  setNotices: (owner, value) =>
    set((state) => {
      const index = state.notices.findIndex((entry) => entry.owner === owner)
      if (index === -1) return { notices: [...state.notices, { owner, value }] }
      const notices = [...state.notices]
      notices[index] = { owner, value }
      return { notices }
    }),
  clearNotices: (owner) =>
    set((state) =>
      state.notices.some((entry) => entry.owner === owner)
        ? { notices: state.notices.filter((entry) => entry.owner !== owner) }
        : {}
    ),
}))
